import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Circle, Lock, Award, Download, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fileUrl, videoEmbed, downloadFile } from "@/lib/training";
import { Markdown } from "@/components/Markdown";
import { ProgressBar } from "@/components/training/TrainingBits";

function LessonBody({ lesson }) {
  if (lesson.type === "video") {
    const embed = videoEmbed(lesson.video_url);
    return (
      <div className="space-y-6">
        <div className="aspect-video overflow-hidden rounded-2xl bg-lasso-ink" data-testid="lesson-video">
          {embed ? (
            <iframe src={embed} title={lesson.title} className="h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen />
          ) : lesson.file_id ? (
            <video src={fileUrl(lesson.file_id)} controls className="h-full w-full" />
          ) : (
            <a href={lesson.video_url} target="_blank" rel="noreferrer" className="flex h-full items-center justify-center text-white underline">Open video</a>
          )}
        </div>
        {lesson.content && <Markdown>{lesson.content}</Markdown>}
      </div>
    );
  }
  if (lesson.type === "pdf") {
    return (
      <div className="space-y-6">
        {lesson.content && <Markdown>{lesson.content}</Markdown>}
        <button onClick={() => downloadFile(`/files/${lesson.file_id}`, lesson.file_name || "lesson.pdf")} className="inline-flex items-center gap-2 rounded-full border border-lasso-line bg-white px-4 py-2 text-sm font-semibold hover:border-lasso-ink" data-testid="lesson-pdf-download">
          <Download className="h-4 w-4" /> Download PDF
        </button>
        <iframe src={fileUrl(lesson.file_id)} title={lesson.title} className="h-[75vh] w-full rounded-2xl border border-lasso-line bg-white" data-testid="lesson-pdf-viewer" />
      </div>
    );
  }
  return <Markdown>{lesson.content}</Markdown>;
}

export default function LessonPlayer() {
  const { id, lessonId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data));
  }, [id]);

  useEffect(() => {
    api.post(`/courses/${id}/resume`, { lesson_id: lessonId }).catch(() => {});
    window.scrollTo({ top: 0 });
  }, [id, lessonId]);

  if (!course) return null;
  const idx = course.lessons.findIndex((l) => l.id === lessonId);
  const lesson = course.lessons[idx];
  if (!lesson) return <p>Lesson not found.</p>;
  const p = course.progress;
  const done = new Set(p?.completed_lesson_ids || []);
  const next = course.lessons[idx + 1];
  const prev = course.lessons[idx - 1];

  const completeAndContinue = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/courses/${id}/lessons/${lesson.id}/complete`);
      const doneNow = new Set(data.completed_lesson_ids);
      setCourse({ ...course, progress: data, quiz: { ...course.quiz, unlocked: course.lessons.every((l) => doneNow.has(l.id)) || data.status === "completed" } });
      const remaining = course.lessons.find((l) => !doneNow.has(l.id));
      if (next) navigate(`/courses/${id}/learn/${next.id}`);
      else if (remaining) navigate(`/courses/${id}/learn/${remaining.id}`);
      else if (course.quiz.question_count && data.status !== "completed") navigate(`/courses/${id}/quiz`);
      else {
        toast.success("Course complete");
        navigate(`/courses/${id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
      <aside className="lg:sticky lg:top-10 lg:self-start" data-testid="lesson-sidebar">
        <Link to={`/courses/${id}`} className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 hover:text-lasso-ink" data-testid="lesson-back-to-course"><ArrowLeft className="h-4 w-4" /> Course overview</Link>
        <p className="mt-6 font-display text-sm font-bold leading-snug">{course.title}</p>
        <div className="mt-3 flex items-center gap-3 text-xs font-semibold"><ProgressBar pct={p?.pct || 0} /><span data-testid="lesson-progress-pct">{p?.pct || 0}%</span></div>
        <ol className="mt-6 space-y-1">
          {course.lessons.map((l, i) => (
            <li key={l.id}>
              <Link to={`/courses/${id}/learn/${l.id}`} data-testid={`lesson-nav-${i + 1}`} className={`flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${l.id === lessonId ? "bg-lasso-ink text-white" : "text-zinc-600 hover:bg-white"}`}>
                {done.has(l.id) || p?.status === "completed" ? <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${l.id === lessonId ? "text-lasso-sky" : "text-emerald-600"}`} /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 opacity-40" />}
                <span className="line-clamp-2">{l.title}</span>
              </Link>
            </li>
          ))}
          {course.quiz.question_count > 0 && (
            <li>
              {course.quiz.unlocked ? (
                <Link to={`/courses/${id}/quiz`} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold text-lasso-deep hover:bg-white" data-testid="lesson-nav-quiz"><Award className="h-4 w-4" /> Final quiz</Link>
              ) : (
                <span className="flex items-center gap-3 px-3 py-2.5 text-sm text-zinc-400" data-testid="lesson-nav-quiz-locked"><Lock className="h-4 w-4" /> Final quiz (locked)</span>
              )}
            </li>
          )}
        </ol>
      </aside>

      <article className="fade-up min-w-0" key={lesson.id}>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-lasso-deep">Lesson {idx + 1} of {course.lessons.length} · {lesson.type} · {lesson.minutes} min</p>
        <h1 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl" data-testid="lesson-title">{lesson.title}</h1>
        <div className="mt-8"><LessonBody lesson={lesson} /></div>
        <div className="mt-12 flex flex-wrap items-center justify-between gap-3 border-t border-lasso-line pt-6">
          {prev ? <Link to={`/courses/${id}/learn/${prev.id}`} className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-500 hover:text-lasso-ink" data-testid="lesson-prev-button"><ArrowLeft className="h-4 w-4" /> Previous</Link> : <span />}
          <button onClick={completeAndContinue} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-lasso-ink px-6 py-3 text-sm font-semibold text-white hover:bg-lasso-navy disabled:opacity-60" data-testid="lesson-complete-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : done.has(lesson.id) ? "Continue" : "Mark complete & continue"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </article>
    </div>
  );
}
